import { Injectable, Injector } from '@angular/core';
import { config } from '@core/interfaces/api_baseurl';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AccountDetails, Bank, BankDetails } from './connect-bank-types';
import { StorageService } from '@core/storage-module/storage.service';
import {
  ConfirmationModalConfig,
  ConfirmationModalService,
} from '@core/customCDK/cdkCustomMdl/services/confirmation-modal.service';
import { ConfirmationComponent } from '@core/customCDK/cdkCustomMdl/components/confirmation/confirmation.component';
import { CredentialsService } from '@page/user-profile/api-access-control/credentials/helper/credentials.service';
import { ApiService } from '@core/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectBankService {
  private _selectedBank: Bank | null = null;

  get selectedBank() {
    return this._StorageService.hasKey('selectedBank')
      ? this._StorageService.getItem('selectedBank')
      : null;
  }
  set selectedBank(v: Bank | null) {
    if (v) {
      this._StorageService.setItem('selectedBank', v);
    } else {
      this._StorageService.removeItem('selectedBank');
    }
    this._selectedBank = v;
  }

  private _isBankModuleActive: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  get isBankModuleActive(): string {
    return this._isBankModuleActive.value;
  }
  set isBankModuleActive(v: string) {
    this._isBankModuleActive.next(v);
  }

  public _allBankDetailList: BehaviorSubject<BankDetails[] | []> =
    new BehaviorSubject<BankDetails[] | []>([]);
  private _selectedBankId: BehaviorSubject<number | ''> = new BehaviorSubject<
    number | ''
  >('');
  private _bankAllAccountList: BehaviorSubject<AccountDetails | null> =
    new BehaviorSubject<AccountDetails | null>(null);

  _filterBank: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _serviceTypeList: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  topPenal: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  bankListData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _allBankDetailListFetchedTime: BehaviorSubject<Date | null> =
    new BehaviorSubject<Date | null>(null);
  get allBankDetailListFetchedTime$(): Observable<Date | null> {
    return this._allBankDetailListFetchedTime.asObservable();
  }
  get bankListData$() {
    return this.bankListData.asObservable();
  }
  get allBankDetailList(): BankDetails[] | [] {
    return this._allBankDetailList.getValue() as BankDetails[] | [];
  }

  get allBankDetailList$(): Observable<BankDetails[] | []> {
    return this._allBankDetailList.asObservable() as Observable<
      BankDetails[] | []
    >;
  }

  get bankAllAccountList(): AccountDetails | null {
    return this._bankAllAccountList.getValue() as AccountDetails | null;
  }

  get bankAllAccountList$(): Observable<AccountDetails | null> {
    return this._bankAllAccountList.asObservable() as Observable<AccountDetails | null>;
  }

  get bankId(): number | '' {
    return this._selectedBankId.value;
  }

  set bankId(v: number | '') {
    this._StorageService.setItem('bankId', v.toString());
    this._selectedBankId.next(v);
  }

  constructor(
    private _ApiService: ApiService,
    private _StorageService: StorageService,
    private _ConfirmationModalService: ConfirmationModalService,
    private Injector: Injector
  ) {
    let bankId = this._StorageService.getItem('bankId');
    let allBankDetailList = this._StorageService.getItem('allBankDetailList');
    let bankAccountList = this._StorageService.getItem('bankAccountList');
    let allBankDetailListFetchedTime = this._StorageService.getItem(
      'allBankDetailListFetchedTime'
    );

    if (bankId) {
      this.bankId = +(bankId as string);
    }
    if (bankAccountList) {
      this._bankAllAccountList.next(bankAccountList);
    }
    if (allBankDetailList) {
      this._allBankDetailList.next(allBankDetailList);
    }
    if (allBankDetailListFetchedTime) {
      this._allBankDetailListFetchedTime.next(
        new Date(allBankDetailListFetchedTime as string)
      );
    }
  }

  resetBankDetail() {
    this.bankId = '';
    this._bankAllAccountList.next(null);
    this._StorageService.removeItem('allBankDetailList');
    this._StorageService.removeItem('bankAccountList');
    this._StorageService.removeItem('bankId');
  }

  getAllBankDetailsList(newFetch: boolean = false): Observable<any> {
    const now = new Date();
    if (this.allBankDetailList.length > 0 && !newFetch && sessionStorage.getItem('allBankDetailList') )
      return of(this.allBankDetailList).pipe(
        tap(() => {
          this._allBankDetailList.next(this.allBankDetailList);
        })
      );
    return this._ApiService.post(config.verify_dashboard.dashboard_api).pipe(
      tap((r: any) => {
        if (r.statuscode == 200) {
          if (r.data.length === 1) {
            this.bankId = (r.data[0] as BankDetails).bank.id;
          }
          let data = r.data;
          // this._StorageService.setItem('allBankDetailList', data);
          // this._allBankDetailList.next(r.data);
          this._StorageService.setItem(
            'allBankDetailListFetchedTime',
            now.toISOString()
          );
          sessionStorage.setItem('allBankDetailList', JSON.stringify(data));
          this._allBankDetailList.next(data);
          this._allBankDetailListFetchedTime.next(now);
        }

        if (r.code == 201) {
          this.handleMissingCredentials(r.message);
          if (newFetch) {
            sessionStorage.removeItem('allBankDetailList');
            this._StorageService.removeItem('allBankDetailListFetchedTime'); // NEW: remove timestamp if clearing list
            this._allBankDetailList.next([]);
            this._allBankDetailListFetchedTime.next(null);
          }
        }
      })
    );
  }

  private handleMissingCredentials(message: string): void {
    const customConfig: ConfirmationModalConfig = {
      data: {
        icon: 'info',
        msg: message,
        btnClose: 'Add Credentials',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCancelButton: false,
      },
    };

    this._ConfirmationModalService
      .init(ConfirmationComponent, customConfig)
      .subscribe((events: any) => {
        this.Injector.get(CredentialsService).GenerateAPIKey({
          showCloseIcon: false,
        });
        events.initRef.close();
      });
  }

  getBankList(): Observable<any> {
    return this._ApiService.post(config.createBank.getbanks);
  }

  getBankDtl(id: string): Observable<any> {
    const formData = { bank_id: id, type: 'details' };
    return this._ApiService.post(config.createBank.getBankForm, formData);
  }

  getBankAllAccountList(
    id: string,
    newFetch: boolean = false
  ): Observable<any> {
    if (this.bankAllAccountList && this.bankId == id && !newFetch)
      return of(this.bankAllAccountList).pipe(
        tap(() => {
          this._bankAllAccountList.next(this.bankAllAccountList);
        })
      );

    const formData = { bank_id: id };
    return this._ApiService
      .skipLoader('post', config.verify_dashboard.single_account, formData)
      .pipe(
        switchMap((r: any) => {
          if (r.statuscode == 200) {
            this._bankAllAccountList.next(r.data);
            this._StorageService.setItem(
              'bankAccountList',
              this.bankAllAccountList
            );
            this.bankId = +id;
          }
          return of(r.data ?? null);
        })
      );
  }

  getServiceTypeList(id: string): Observable<any> {
    const formData = { bank_id: id };
    return this._ApiService.post(config.createBank.get_account_type, formData);
  }

  addBankAc(url: string, formData: any): Observable<any> {
    return this._ApiService.post(url, formData);
  }

  dynamicFork(id: string) {
    return forkJoin([this.getBankDtl(id), this.getServiceTypeList(id)]);
  }

  getAccountsList(obj: any) {
    return this._ApiService.post(config.createBank.userAccountList, obj).pipe(
      switchMap((res: any) => {
        this._filterBank.next(res);
        return of(res);
      })
    );
  }

  getAccountDetails(formData: any): Observable<any> {
    return this._ApiService.post(config.createBank.getAccountDetails, formData);
  }

  getBankServiceTypeAndBankDtlFork(data: any[]) {
    return forkJoin([
      this.getAccountDetails(data[0]),
      this.getServiceTypeList(data[1]),
    ]);
  }

  getCrednitialForm(formData: any): Observable<any> {
    return this._ApiService.post(
      config.bankCrednitial.getAddBankForm,
      formData
    );
  }

  addCrednitial(formData: any): Observable<any> {
    return this._ApiService.post(
      config.bankCrednitial.addBankCrednitial,
      formData
    );
  }

  getCrednitial(formData: any): Observable<any> {
    return this._ApiService.post(
      config.bankCrednitial.viewCredential,
      formData
    );
  }

  editKey(formData: any): Observable<any> {
    return this._ApiService.post(
      config.bankCrednitial.editCrednitial,
      formData
    );
  }

  topPanel(bankId: any) {
    const formdata = { bank_id: bankId, type: 'services' };
    return this._ApiService.post(config.sidemenu.topPanel, formdata).pipe(
      switchMap((res: any) => {
        if (res.code == 200) {
          this.topPenal.next(res.data);
        }
        return of(res);
      })
    );
  }

  getPayoutNewReports(formdata: any) {
    return this._ApiService.post(config.reportsNew.payoutNew, formdata);
  }
  downloadFile(formdata: any) {
    return this._ApiService.post(config.reportsNew.download, formdata);
  }
  downloadWalletStatment(formdata: any) {
    return this._ApiService.post(
      config.walletStatmentReports.walletStatmentDownload,
      formdata
    );
  }
  getWalletTransaction(formdata: any) {
    return this._ApiService.post(
      config.walletStatmentReports.walletTransaction,
      formdata
    );
  }
  getEcollectReport(formdata: any) {
    return this._ApiService.post(config.ecollect.ecollectReport, formdata);
  }
  ecollectDownload(formdata: any) {
    return this._ApiService.post(config.ecollect.ecollectDownload, formdata);
  }
  bankList() {
    return this._ApiService.post(config.reportsNew.filter);
  }
  upiCollectionReport(formdata: any) {
    return this._ApiService.post(
      config.upiCollection.upiCollectionReport,
      formdata
    );
  }
  upiCollectionDownload(formdata: any) {
    return this._ApiService.post(
      config.upiCollection.upiCollectionExport,
      formdata
    );
  }
  getBeneficiaryReport(formdata: any) {
    return this._ApiService.post(
      config.beneficiary.beneficiaryReport,
      formdata
    );
  }
  downloadBenificaryReport(formdata: any) {
    return this._ApiService.post(
      config.beneficiary.beneficiaryExport,
      formdata
    );
  }
  getTxnIssues(formdata: any) {
    return this._ApiService.post(config.reportsNew.getTxnIssues, formdata);
  }
  getVpaReport(formdata: any) {
    return this._ApiService.post(config.vpacreation.getVpaReport, formdata);
  }
  exportVpaReport(formdata: any) {
    return this._ApiService.post(config.vpacreation.exportVpaReport, formdata);
  }
}
